import React, { useState } from 'react';
import { X, Upload, Link, Calendar, AlertCircle, CheckCircle, Loader } from 'lucide-react';
import { calendarImportService } from '../../lib/calendar-import-service';

export default function CalendarImportModal({ isOpen, onClose, onImportComplete }) {
  const [importType, setImportType] = useState('url'); // 'url' or 'file'
  const [icalUrl, setIcalUrl] = useState('');
  const [selectedFile, setSelectedFile] = useState(null);
  const [calendarName, setCalendarName] = useState('');
  const [category, setCategory] = useState('external');
  const [isImporting, setIsImporting] = useState(false);
  const [importResults, setImportResults] = useState(null);
  const [error, setError] = useState('');

  // Common school district calendar URLs (examples)
  const commonUrls = [
    {
      name: 'Example School District',
      url: 'https://calendar.google.com/calendar/ical/example@group.calendar.google.com/public/basic.ics',
      category: 'school'
    }
  ];

  const categories = [
    { value: 'school', label: 'School District', color: 'bg-azure-100 text-azure-800' },
    { value: 'sports', label: 'Sports', color: 'bg-emerald-100 text-emerald-800' },
    { value: 'community', label: 'Community Events', color: 'bg-violet-100 text-violet-800' },
    { value: 'external', label: 'Other External', color: 'bg-stone-100 text-slate-800' }
  ];

  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (file && file.name.endsWith('.ics')) {
      setSelectedFile(file);
      if (!calendarName) {
        setCalendarName(file.name.replace('.ics', ''));
      }
      setError('');
    } else {
      setError('Please select a valid .ics calendar file');
    }
  };

  const handleImport = async () => {
    if (!calendarName.trim()) {
      setError('Please enter a calendar name');
      return;
    }

    setIsImporting(true);
    setError('');
    setImportResults(null);

    try {
      let icalContent;

      if (importType === 'url') {
        if (!icalUrl.trim()) {
          throw new Error('Please enter a calendar URL');
        }
        icalContent = icalUrl.trim();
      } else {
        if (!selectedFile) {
          throw new Error('Please select a calendar file');
        }
        icalContent = await selectedFile.text();
      }

      const results = await calendarImportService.importFromIcal(icalContent, {
        source: importType === 'url' ? 'ical_url' : 'ical_file',
        calendarName: calendarName.trim(),
        category,
        familyId: 'single-family'
      });

      setImportResults(results);
      
      if (results.imported > 0 && onImportComplete) {
        onImportComplete(results);
      }

    } catch (err) {
      console.error('Import error:', err);
      setError(err.message || 'Failed to import calendar');
    } finally {
      setIsImporting(false);
    }
  };

  const handleReset = () => {
    setImportType('url');
    setIcalUrl('');
    setSelectedFile(null);
    setCalendarName('');
    setCategory('external');
    setIsImporting(false);
    setImportResults(null);
    setError('');
  };

  const handleClose = () => {
    handleReset();
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-stone-200">
          <div className="flex items-center gap-3">
            <Calendar className="w-6 h-6 text-azure-600" />
            <h2 className="text-xl font-semibold text-slate-900">Import External Calendar</h2>
          </div>
          <button
            onClick={handleClose}
            className="p-2 hover:bg-stone-100 rounded-lg transition-colors"
          >
            <X className="w-5 h-5 text-stone-500" />
          </button>
        </div>

        <div className="p-6 space-y-6">
          {/* Import Results */}
          {importResults && (
            <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4">
              <div className="flex items-center gap-2 mb-2">
                <CheckCircle className="w-5 h-5 text-emerald-600" />
                <h3 className="font-medium text-emerald-900">Import Complete</h3>
              </div>
              <div className="text-sm text-emerald-800 space-y-1">
                <p>✅ {importResults.imported} events imported successfully</p>
                {importResults.skipped > 0 && (
                  <p>⏭️ {importResults.skipped} events skipped (already exist)</p>
                )}
                {importResults.errors.length > 0 && (
                  <p>❌ {importResults.errors.length} events failed to import</p>
                )}
              </div>
              <button
                onClick={handleClose}
                className="mt-3 px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors"
              >
                Done
              </button>
            </div>
          )}

          {/* Error Display */}
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <div className="flex items-center gap-2">
                <AlertCircle className="w-5 h-5 text-red-600" />
                <p className="text-red-800">{error}</p>
              </div>
            </div>
          )}

          {!importResults && (
            <>
              {/* Import Type Selection */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-3">
                  Import Method
                </label>
                <div className="grid grid-cols-2 gap-4">
                  <button
                    onClick={() => setImportType('url')}
                    className={`p-4 rounded-lg border-2 transition-colors ${
                      importType === 'url'
                        ? 'border-azure-500 bg-azure-50'
                        : 'border-stone-200 hover:border-stone-300'
                    }`}
                  >
                    <Link className="w-6 h-6 mx-auto mb-2 text-azure-600" />
                    <div className="text-sm font-medium">Calendar URL</div>
                    <div className="text-xs text-stone-500">iCal/Google Calendar URL</div>
                  </button>
                  <button
                    onClick={() => setImportType('file')}
                    className={`p-4 rounded-lg border-2 transition-colors ${
                      importType === 'file'
                        ? 'border-azure-500 bg-azure-50'
                        : 'border-stone-200 hover:border-stone-300'
                    }`}
                  >
                    <Upload className="w-6 h-6 mx-auto mb-2 text-azure-600" />
                    <div className="text-sm font-medium">Upload File</div>
                    <div className="text-xs text-stone-500">.ics calendar file</div>
                  </button>
                </div>
              </div>

              {/* URL Input */}
              {importType === 'url' && (
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Calendar URL
                  </label>
                  <input
                    type="url"
                    value={icalUrl}
                    onChange={(e) => setIcalUrl(e.target.value)}
                    placeholder="https://calendar.google.com/calendar/ical/..."
                    className="w-full px-3 py-2 border border-stone-300 rounded-lg focus:ring-2 focus:ring-azure-500 focus:border-azure-500"
                  />
                  <p className="text-xs text-stone-500 mt-1">
                    Enter the iCal URL from Google Calendar, Outlook, or other calendar service
                  </p>
                  
                  {/* Common URLs */}
                  {commonUrls.length > 0 && (
                    <div className="mt-3">
                      <p className="text-sm font-medium text-slate-700 mb-2">Quick Select:</p>
                      <div className="space-y-1">
                        {commonUrls.map((cal, index) => (
                          <button
                            key={index}
                            onClick={() => {
                              setIcalUrl(cal.url);
                              setCalendarName(cal.name);
                              setCategory(cal.category);
                            }}
                            className="text-sm text-azure-600 hover:text-azure-800 block"
                          >
                            {cal.name}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* File Input */}
              {importType === 'file' && (
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Select Calendar File
                  </label>
                  <input
                    type="file"
                    accept=".ics"
                    onChange={handleFileSelect}
                    className="w-full px-3 py-2 border border-stone-300 rounded-lg focus:ring-2 focus:ring-azure-500 focus:border-azure-500"
                  />
                  {selectedFile && (
                    <p className="text-sm text-emerald-600 mt-1">
                      Selected: {selectedFile.name}
                    </p>
                  )}
                </div>
              )}

              {/* Calendar Name */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Calendar Name
                </label>
                <input
                  type="text"
                  value={calendarName}
                  onChange={(e) => setCalendarName(e.target.value)}
                  placeholder="e.g., School District Calendar"
                  className="w-full px-3 py-2 border border-stone-300 rounded-lg focus:ring-2 focus:ring-azure-500 focus:border-azure-500"
                />
              </div>

              {/* Category Selection */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Category
                </label>
                <div className="grid grid-cols-2 gap-2">
                  {categories.map((cat) => (
                    <button
                      key={cat.value}
                      onClick={() => setCategory(cat.value)}
                      className={`p-3 rounded-lg border-2 transition-colors text-left ${
                        category === cat.value
                          ? 'border-azure-500 bg-azure-50'
                          : 'border-stone-200 hover:border-stone-300'
                      }`}
                    >
                      <div className={`inline-block px-2 py-1 rounded-full text-xs font-medium mb-1 ${cat.color}`}>
                        {cat.label}
                      </div>
                    </button>
                  ))}
                </div>
              </div>

              {/* Actions */}
              <div className="flex justify-end gap-3 pt-4">
                <button
                  onClick={handleClose}
                  className="px-4 py-2 text-slate-700 border border-stone-300 rounded-lg hover:bg-stone-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleImport}
                  disabled={isImporting}
                  className="px-4 py-2 bg-azure-600 text-white rounded-lg hover:bg-azure-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-2"
                >
                  {isImporting && <Loader className="w-4 h-4 animate-spin" />}
                  {isImporting ? 'Importing...' : 'Import Calendar'}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}